import { Button, FormLabel, Modal, TextArea, Text, Alert } from "@oysterhr/ui"
import { NewSelectOysterAdmin } from "~/shared/components/NewOysterAdminSelect"
import * as Yup from "yup"
import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { Mutation, MutationDisableOffboardingESignArgs } from "@oysterhr/schema"
import { gql, useMutation } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { AgreementDetailsFragmentType } from "@oysterhr/hiring/employment-agreements"

interface DisableOffboardingESignValues {
  approverId: string
  notes: string
}

interface DisableOffboardingESignModalProps {
  agreement: AgreementDetailsFragmentType
  onClose: () => void
}

const validationSchema = Yup.object().shape({
  approverId: Yup.string().required("Select an approver"),
  notes: Yup.string().trim().required("Leave a note for the approver"),
})

export const DISABLE_OFFBOARDING_E_SIGN = gql`
  mutation DisableOffboardingESign($input: DisableOffboardingESignInput!) {
    disableOffboardingESign(input: $input) {
      owner {
        ... on Termination {
          id
          agreements {
            id
            agreementType
            isESignEnabled
          }
        }
      }
      errors
    }
  }
`

export const DisableOffboardingESignModal = ({
  agreement,
  onClose,
}: DisableOffboardingESignModalProps) => {
  // Hooks
  const { t } = useTranslation("Agreements", { keyPrefix: "disableOffboardingESign" })
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm<DisableOffboardingESignValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      approverId: "",
      notes: "",
    },
  })

  // GraphQL
  const [disableOffboardingESign, { loading }] = useMutation<
    {
      disableOffboardingESign: Mutation["disableOffboardingESign"]
    },
    MutationDisableOffboardingESignArgs
  >(DISABLE_OFFBOARDING_E_SIGN, {
    onCompleted: () => {
      onClose()
    },
  })

  // Actions
  const onSubmit = (data: DisableOffboardingESignValues) => {
    const { approverId, notes } = data
    disableOffboardingESign({
      variables: {
        input: {
          agreementId: agreement.id,
          approverId,
          notes,
        },
      },
    })
  }

  if (!agreement) return null

  return (
    <Modal isOpen={true} onRequestClose={onClose}>
      <Modal.Header>{t("title")}</Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content className="space-y-4">
          <Text>{t("content.header")}</Text>
          <Alert variation="warning">
            <Alert.Icon />
            <Alert.Heading>{t("content.alert.title")}</Alert.Heading>
            <Alert.Content>
              <ul className="ml-6 list-disc">
                <li>{t("content.alert.eSignDisabledInfo")}</li>
                <li>{t("content.alert.signatureCollectionInfo")}</li>
              </ul>
            </Alert.Content>
          </Alert>
          <Controller
            name="approverId"
            control={control}
            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
              <NewSelectOysterAdmin
                label={<FormLabel label={t("form.approver.label")} />}
                placeholder={t("form.approver.placeholder")}
                onChange={onChange}
                value={value}
                name={name}
                error={error?.message}
              />
            )}
          />
          <TextArea
            label={
              <FormLabel
                label={t("form.notes.label")}
                assistiveText={t("form.notes.assistiveText")}
              />
            }
            error={errors?.notes?.message}
            {...register("notes")}
          />
        </Modal.Content>
        <Modal.Footer>
          <Button variation="secondary" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isLoading={loading} disabled={loading}>
            {t("buttons.disableESign")}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
